import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import axios from 'axios'
import { useFlash } from 'src/biz_spa/composables'
import { castAttributesForCamelCase, castAttributesForSnakeCase } from 'src/biz_spa/modules/string_converter'
import type { CsvImportErrorStatusType, CsvErrorAttribute, CsvErrorReason } from '../../../domains/foodMenu/types'

import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

export type ReadFoodMenuCsvFilesApiParams = {
  per: number,
  page: number,
  sort_order: 'asc' | 'desc',
}

export type ImportError = {
  lineNumber: number | null;
  attr: CsvErrorAttribute;
  reason: CsvErrorReason;
};

export type FoodMenuCsvFile = {
  id: number;
  importStatus: CsvImportErrorStatusType
  originalFilename: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  createdAt: string;
  importErrors: ImportError[];
}

export type FoodMenuCsvFileMeta = {
  totalCount: number,
  totalPages: number,
}

export const useFoodMenuCsvFileStore = defineStore('foodMenuCsvFile', () => {
  const { updateFlash } = useFlash()
  const route = useRoute()
  const { t } = useI18n()
  const companyUuid = computed<string>(() => route.params.company_uuid as string)
  const foodMenuCsvFiles = ref<FoodMenuCsvFile[]>([])
  const foodMenuCsvFilesMeta = ref<FoodMenuCsvFileMeta>({
    totalCount: 0,
    totalPages: 0,
  })
  const isLoading = ref<boolean>(false)
  const startLoading = () => {
    isLoading.value = true
  }

  // 一覧取得 API
  const readFoodMenuCsvFiles = async (params : ReadFoodMenuCsvFilesApiParams): Promise<void> => {
    if (isLoading.value) return
    startLoading()

    return axios
      .get(`/api/companies/${companyUuid.value}/food_menus/food_menu_files.json`, castAttributesForSnakeCase(params))
      .then(res => {
        foodMenuCsvFiles.value = res.data.data.map((v: FoodMenuCsvFile) => castAttributesForCamelCase(v)) as FoodMenuCsvFile[]
        foodMenuCsvFilesMeta.value = castAttributesForCamelCase(res.data.meta) as FoodMenuCsvFileMeta
      })
      .catch(err => {
        updateFlash({ type: 'alert', message: err.response.data.errors[0].detail })
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  // テンプレートファイルのダウンロード
  const downloadTemplate = async (): Promise<void> => {
    if (isLoading.value) return
    startLoading()

    return axios
      .get(`/api/companies/${companyUuid.value}/food_menus/food_menu_file_templates`, {
        responseType: 'arraybuffer',
      })
      .then(res => {
        const link = document.createElement('a')
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }))
        link.download = 'template.zip'
        link.click()
      })
      .catch(err => {
        updateFlash({ type: 'alert', message: err.response.data.errors[0].detail })
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  // CSVアップロード
  const uploadFile = async ({ data }: { data: FormData }): Promise<boolean> => {
    if (isLoading.value) return false
    startLoading()

    return axios
      .post(`/api/companies/${companyUuid.value}/food_menus/food_menu_files.json`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      })
      .then(() => {
        updateFlash({ type: 'notice', message: t('messages.upload', { value: t('labels.csv') }) })
        return true
      })
      .catch(err => {
        updateFlash({ type: 'alert', message: err.response.data.errors[0].detail })
        return false
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  // アップロード済みCSVのダウンロード
  const downloadFile = async ({ id, filename }: { id: number, filename: string }): Promise<void> => {
    if (isLoading.value) return
    startLoading()

    return axios
      .get(`/api/companies/${companyUuid.value}/food_menus/food_menu_files/${id}`, {
        responseType: 'arraybuffer',
      })
      .then(res => {
        const link = document.createElement('a')
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/csv' }))
        link.download = filename
        link.click()
      })
      .catch(err => {
        updateFlash({ type: 'alert', message: err.response.data.errors[0].detail })
      })
      .finally(() => {
        isLoading.value = false
      })
  }
  // アップロード済みCSVのエラーファイルの削除
  const deleteFile = async ({ id }: { id: number }): Promise<void> => {
    if (isLoading.value) return
    startLoading()

    axios
      .delete(`/api/companies/${companyUuid.value}/food_menus/food_menu_files/${id}`)
      .then(() => {
        foodMenuCsvFiles.value = foodMenuCsvFiles.value.filter(v => v.id !== id)
        updateFlash({ type: 'notice', message: t('messages.deleted', { value: t('labels.csv') }) })
      })
      .catch(err => {
        updateFlash({ type: 'alert', message: err.response.data.errors[0].detail })
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  return {
    foodMenuCsvFiles,
    foodMenuCsvFilesMeta,
    isLoading,
    readFoodMenuCsvFiles,
    downloadTemplate,
    uploadFile,
    downloadFile,
    deleteFile
  }
})
